import { useLazyQuery, useMutation } from '@apollo/client/react';
import { TestDataService } from '@teamexos/test-data-service';
import { chunk, sum } from 'lodash';
import { useState } from 'react';
import {
  AdminCreateStubUsersDocument,
  AdminDeleteUserDocument,
  AdminDeleteUsersDocument,
  AdminGetUserByEmailQuery,
  AdminGetUserDocument,
  AdminListCoachUsersDocument,
  AdminListCustomPlanTemplatesDocument,
  AdminManyClientsQuery,
  AdminUpdateUserDocument,
  Prefix,
} from '../../../graphql/types';
import { useUploadFile } from '../../../hooks/useUploadFile';
import { AdminDemoUserInput } from './data/coachTraining';
import {
  getSalesDemoMembers,
  SALES_DEMO_EMPLOYERS,
} from './data/salesDemoData';
import { useActionLog } from './utils';

const STUB_USER_COUNT = 500;
const MEMBER_CREATION_CHUNK_SIZE = 3;
const MEMBER_DELETION_CHUNK_SIZE = 20;
const NUTRITIONIST_ID = '4458c448-20c1-70f7-95e3-8b23305f1104'; // https://admin.dev.prince.ex-os.net/app/User/4458c448-20c1-70f7-95e3-8b23305f1104/show

type ExistingCoach = AdminGetUserByEmailQuery['data'];
type Coach = NonNullable<ExistingCoach>;
type RequiredClient = AdminManyClientsQuery['data'][number];

interface UseSalesDemoActionsArgs {
  existingCoach: ExistingCoach;
  requiredClients: RequiredClient[];
  actionLog: ReturnType<typeof useActionLog>;
  refetchRequiredClientMembers: () => Promise<unknown>;
}

// Create/clear actions for sales demo members. The per-client variants share a
// core that operates on a list of clients. All actions report through the given
// action log.
export const useSalesDemoActions = ({
  existingCoach,
  requiredClients,
  actionLog,
  refetchRequiredClientMembers,
}: UseSalesDemoActionsArgs) => {
  const {
    logAction,
    setMemberCreationStatus,
    setMemberCreationProgress,
    setErrors,
  } = actionLog;

  const [isMutating, setIsMutating] = useState(false);

  const [updateUser] = useMutation(AdminUpdateUserDocument);
  const { uploadImage } = useUploadFile();
  const [createStubUsers] = useMutation(AdminCreateStubUsersDocument);
  const [listCoachUsers] = useLazyQuery(AdminListCoachUsersDocument, {
    fetchPolicy: 'no-cache',
  });
  const [listCustomPlanTemplates] = useLazyQuery(
    AdminListCustomPlanTemplatesDocument,
  );
  const [getUser] = useLazyQuery(AdminGetUserDocument, {
    fetchPolicy: 'network-only',
  });
  const [deleteUser] = useMutation(AdminDeleteUserDocument);
  const [deleteUsers] = useMutation(AdminDeleteUsersDocument);

  // Every action requires an existing coach and toggles the shared mutation
  // flag around its work.
  const runAction = async (action: (coach: Coach) => Promise<void>) => {
    if (!existingCoach?.id) {
      return;
    }
    setIsMutating(true);
    try {
      await action(existingCoach);
    } finally {
      setIsMutating(false);
    }
  };

  const clientsById = (clientId: string) =>
    requiredClients.filter((client) => client.id === clientId);

  const createStubUsersForClients = (clients: RequiredClient[]) =>
    runAction(async (coach) => {
      for await (const client of clients) {
        await logAction(
          `Creating stub users for ${client.displayName}`,
          async () => {
            await createStubUsers({
              variables: {
                input: {
                  clientId: client.id,
                  coachId: coach.id,
                  count: STUB_USER_COUNT,
                },
              },
            });
          },
        );
        await refetchRequiredClientMembers();
      }
    });

  const createStubUsersForClient = (clientId: string) =>
    createStubUsersForClients(clientsById(clientId));

  // The app's office picker only lists a client's locations that have a coach
  // of that client assigned, so connecting a client also assigns the coach to
  // the client's demo location. `locationIds` replaces the coach's full set, so
  // fetch their current locations fresh and add this client's.
  const getCoachLocationIds = async (coachId: string, clientId: string) => {
    const locationId = SALES_DEMO_EMPLOYERS.find(
      (employer) => employer.id === clientId,
    )?.locationId;
    if (!locationId) {
      return undefined;
    }

    const { data } = await getUser({
      variables: { id: coachId },
    });
    const existingLocationIds =
      data?.data?.coachData?.locations.nodes.map((location) => location.id) ??
      [];
    return existingLocationIds.includes(locationId)
      ? existingLocationIds
      : [...existingLocationIds, locationId];
  };

  const addCoachClient = (clientId: string) =>
    runAction(async (coach) => {
      const existingCoachClients = coach.coachData?.coachClients.edges || [];
      await logAction('Adding coach client and location', async () => {
        const locationIds = await getCoachLocationIds(coach.id, clientId);
        await updateUser({
          variables: {
            coachDataInput: {
              id: coach.id,
              coachClients: [
                ...existingCoachClients.map((c) => ({
                  clientId: c.node.id,
                  supportsHighPerformanceCoaching:
                    c.supportsHighPerformanceCoaching,
                  supportsWellbeingCoaching: c.supportsWellbeingCoaching,
                  availableForAssignment: c.availableForAssignment,
                })),
                {
                  clientId,
                  supportsHighPerformanceCoaching: true,
                  supportsWellbeingCoaching: true,
                  availableForAssignment: true,
                },
              ],
              locationIds,
            },
            updateCoachData: true,
            updateAvatar: false,
            avatarInput: {},
            input: {},
          },
        });
      });
    });

  const fetchGameplanTemplateId = async () => {
    const { data } = await listCustomPlanTemplates({
      variables: { input: { first: 1 } },
    });

    return data?.data.nodes[0].id;
  };

  // Uploads a public avatar image (bundled under `public/`) through the standard
  // signed-url flow and assigns it to the given user. The upload `key` is the
  // avatar id consumed by the `updateAvatar` mutation (see buildQuery.ts).
  const assignAvatar = async (userId: string, imagePath: string) => {
    const response = await fetch(imagePath);
    if (!response.ok) {
      throw new Error(`Failed to fetch avatar image ${imagePath}`);
    }
    const blob = await response.blob();
    const fileName = imagePath.split('/').pop() ?? 'avatar.jpg';
    const file = new File([blob], fileName, { type: blob.type });

    const { key } = await uploadImage({ file, prefix: Prefix.Avatar });

    await updateUser({
      variables: {
        input: { id: userId },
        // Unused: gated off by `updateCoachData: false`, but required by the
        // mutation's variables type.
        coachDataInput: { id: userId },
        updateCoachData: false,
        updateAvatar: true,
        avatarInput: { avatarId: key, userId },
      },
    });
  };

  // Creates the given members, advancing the shared progress bar. Callers are
  // responsible for initializing the progress total beforehand.
  const createPredefinedMembers = async (
    memberData: AdminDemoUserInput[],
    clientId: string,
  ) => {
    const createMember = async (member: AdminDemoUserInput) => {
      try {
        // createDemoUser also joins the configured challenges and seeds the
        // activities that fill their minimums (challengeIds/challengeActivities
        // on the member input).
        const user = await TestDataService.createDemoUser(
          { ...member, fitClientId: clientId },
          logAction,
        );

        // createDemoUser can't set an avatar, so upload the persona's image
        // through the standard signed-url flow and assign it once we have the
        // new user's id (see `avatarImagePath` on the persona data).
        const avatarImagePath = member.avatarImagePath;
        if (avatarImagePath) {
          await logAction('Uploading and assigning avatar', () =>
            assignAvatar(user.id, avatarImagePath),
          );
        }
      } catch {
        setErrors((p) => [...p, `Failed to create member`]);
      } finally {
        setMemberCreationProgress(([prevIndex, prevTotal]) => [
          prevIndex + 1,
          prevTotal,
        ]);
      }
    };

    // create in parallel batches to avoid overwhelming the server
    const memberChunks = chunk(memberData, MEMBER_CREATION_CHUNK_SIZE);
    for await (const memberChunk of memberChunks) {
      await Promise.all(memberChunk.map(createMember));
    }
    await refetchRequiredClientMembers();
  };

  const createPredefinedMembersForClients = (clients: RequiredClient[]) =>
    runAction(async (coach) => {
      setMemberCreationStatus([]);

      const gameplanTemplateId = await logAction(
        'Resolving gameplan template ids',
        fetchGameplanTemplateId,
      );

      const clientMemberData = clients.map((client) => ({
        clientId: client.id,
        memberData: getSalesDemoMembers({
          coachId: coach.id,
          coachEmail: coach.email,
          nutritionistId: NUTRITIONIST_ID,
          fitClientId: client.id,
          gameplanTemplateId,
        }),
      }));

      setMemberCreationProgress([
        0,
        sum(clientMemberData.map(({ memberData }) => memberData.length)),
      ]);
      for await (const { clientId, memberData } of clientMemberData) {
        await createPredefinedMembers(memberData, clientId);
      }
    });

  const createPredefinedMembersForClient = (clientId: string) =>
    createPredefinedMembersForClients(clientsById(clientId));

  // Page through every coachUser at the given client and collect their ids.
  const fetchAllMemberIds = async ({
    coachId,
    clientId,
    isStubUser,
  }: {
    coachId: string;
    clientId: string;
    isStubUser: boolean;
  }): Promise<string[]> => {
    const ids: string[] = [];
    let after: string | undefined;
    let hasNextPage = true;
    while (hasNextPage) {
      const { data } = await listCoachUsers({
        variables: {
          id: coachId,
          filter: {
            clientIds: [clientId],
            isStubUser,
          },
          input: { first: 100, after },
        },
      });
      const connection = data?.user?.coachData?.coachUsers;
      connection?.edges?.forEach((edge) => {
        if (edge.node?.id) {
          ids.push(edge.node.id);
        }
      });
      hasNextPage = connection?.pageInfo?.hasNextPage ?? false;
      after = connection?.pageInfo?.endCursor ?? undefined;
      if (!after) {
        break;
      }
    }
    return ids;
  };

  // Deletes the given members in chunks, advancing the shared progress bar.
  // Callers are responsible for initializing the progress total beforehand.
  const deleteMembersInChunks = async (
    memberIds: string[],
    clientName: string,
    isStubUser: boolean,
  ) => {
    const memberIdChunks = chunk(memberIds, MEMBER_DELETION_CHUNK_SIZE); // delete in batches to avoid overwhelming the server

    let deletedCount = 0;
    for await (const memberIdChunk of memberIdChunks) {
      await logAction(
        `Deleting ${clientName} members ${deletedCount + memberIdChunk.length}/${memberIds.length}`,
        async () => {
          if (isStubUser) {
            await deleteUsers({ variables: { userIds: memberIdChunk } });
          } else {
            // Predefined members must be deleted individually via deleteUser.
            await Promise.all(
              memberIdChunk.map((userId) =>
                deleteUser({ variables: { userId } }),
              ),
            );
          }
        },
      );
      setMemberCreationProgress(([prevIndex, prevTotal]) => [
        prevIndex + memberIdChunk.length,
        prevTotal,
      ]);
      await refetchRequiredClientMembers();
      deletedCount += memberIdChunk.length;
    }
  };

  const clearMembersForClients = (
    clients: RequiredClient[],
    isStubUser: boolean,
  ) =>
    runAction(async (coach) => {
      setErrors([]);
      setMemberCreationStatus([]);
      setMemberCreationProgress([]);

      // Collect member ids across every client first so the progress bar
      // reflects the overall total, then delete client by client.
      const clientMemberIds: { clientName: string; memberIds: string[] }[] = [];
      for await (const client of clients) {
        const memberIds = await logAction(
          `Finding members to clear for ${client.displayName}`,
          () =>
            fetchAllMemberIds({
              coachId: coach.id,
              clientId: client.id,
              isStubUser,
            }),
        );
        clientMemberIds.push({
          clientName: client.displayName,
          memberIds: memberIds ?? [],
        });
      }

      const totalMemberCount = sum(
        clientMemberIds.map(({ memberIds }) => memberIds.length),
      );
      if (totalMemberCount === 0) {
        await refetchRequiredClientMembers();
        return;
      }

      setMemberCreationProgress([0, totalMemberCount]);
      for await (const { clientName, memberIds } of clientMemberIds) {
        if (memberIds.length > 0) {
          await deleteMembersInChunks(memberIds, clientName, isStubUser);
        }
      }
    });

  const clearMembersForClient = ({
    clientId,
    isStubUser,
  }: {
    clientId: string;
    isStubUser: boolean;
  }) => clearMembersForClients(clientsById(clientId), isStubUser);

  return {
    isMutating,
    addCoachClient,
    createStubUsersForClient,
    createPredefinedMembersForClient,
    clearMembersForClient,
  };
};
